import { Get, Put, Post, Patch, Delete } from "./api.helper";
import { store } from "store";
import Config from "Config";
import { appendQueryParams } from "utils/urlUtils";
import { handleFetchError } from "../utils/errorHandler";

// ==== Authentication
export const Login = (bodyData) => {
  return Post({
    url: "/auth/login",
    body: bodyData,
    isAuthorized: false,
  });
};

export const GetMe = () => {
  return Get({ url: "/user/me" });
};

export const ForgotPassword = (bodyData) => {
  return Post({ url: "/auth/forget-password", body: bodyData });
};

export const ResendOTP = (bodyData) => {
  return Post({ url: "/auth/resend-otp", body: bodyData });
};

export const ForgotPasswordVerification = (bodyData) => {
  return Post({
    url: "/auth/forget-password/verification",
    body: bodyData,
  });
};

export const ResetPassword = (bodyData) => {
  return Post({ url: "/auth/reset-password", body: bodyData });
};

export const VerifyUser = (bodyData) => {
  return Post({
    url: "/",
    body: bodyData,
    isAuthorized: false,
  });
};

export const SignUp = (bodyData) => {
  return Post({
    url: "/",
    body: bodyData,
    isAuthorized: false,
  });
};

export const changePassword = (bodyData) => {
  return Post({
    url: "/auth/change-password",
    body: bodyData,
  });
};
// ALPHA LISTING API
export const customerListingApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/users", params, abortSignal });
};

export const userUpdateStatus = ({ id, bodyData }) => {
  return Post({
    url: `/user/${id}/status`,
    body: bodyData,
  });
};

// Grants/revokes one account's access to the standalone MCAP app — see the
// MCAP Access page. Any account type may be the target: MCAP's analytics
// endpoints admit UserType.USER as well as UserType.ADMIN, so a teacher or
// student can be granted the same org-wide MCAP an admin sees.
export const setMcapAccess = ({ id, mcapAccess }) => {
  return Post({
    url: `/user/${id}/mcap-access`,
    body: { mcapAccess },
  });
};

// Grants/revokes one account's access to the WhatsApp inbox in MCAP. Separate
// from MCAP access on purpose: the inbox shows every lead's phone number and
// messages, and MCAP access can go to teacher accounts.
export const setInboxAccess = ({ id, inboxAccess }) => {
  return Post({
    url: `/user/${id}/inbox-access`,
    body: { inboxAccess },
  });
};

// School-administrator requests: someone asking to run a school's account on
// the Mind Circuit school admin portal.
//
// Approving grants read access to that school's aggregate wellbeing data, so the
// listing returns corroborating signal alongside each request rather than just a
// name and a school — see SchoolAdminRequests for what the reviewer is meant to
// compare.
export const schoolAdminRequestsApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/admin/school-admin/requests", params, abortSignal });
};

// reviewNote is required by the server. It records how the reviewer satisfied
// themselves that this person actually runs this school.
export const approveSchoolAdminRequest = ({ id, reviewNote }) => {
  return Post({ url: `/admin/school-admin/requests/${id}/approve`, body: { reviewNote } });
};

// declineReason is emailed to the requester verbatim, so it is written for them
// to read, not as an internal note.
export const declineSchoolAdminRequest = ({ id, declineReason }) => {
  return Post({ url: `/admin/school-admin/requests/${id}/decline`, body: { declineReason } });
};

// Withdraws standing from someone who already has it. The row is kept as
// REVOKED rather than deleted — who held access and who took it away is the
// point of the record.
export const revokeSchoolAdminAccess = ({ id }) => {
  return Post({ url: `/admin/school-admin/requests/${id}/revoke`, body: {} });
};

// Align users (ops only): place an existing account under a school and move its classrooms with it.
// The preview is advice; the server works the move out again from the database when it runs.
// The shared query helper does not encode values, and an email like name+tag@example.com would
// reach the server with its "+" read as a space, so the search text is encoded here.
export const alignUsersSearchApi = ({ q } = {}, abortSignal = null) => {
  return Get({ url: "/admin/alignment/users", params: { q: encodeURIComponent(q ?? "") }, abortSignal });
};

export const alignSchoolsSearchApi = ({ q, state } = {}, abortSignal = null) => {
  return Get({
    url: "/admin/alignment/schools",
    params: { q: encodeURIComponent(q ?? ""), ...(state ? { state } : {}) },
    abortSignal,
  });
};

export const alignPreviewApi = ({ userId, schoolId }) => {
  return Get({ url: "/admin/alignment/preview", params: { userId, schoolId } });
};

export const alignUserApi = ({ userId, schoolId, notify }) => {
  return Post({ url: "/admin/alignment/align", body: { userId, schoolId, notify } });
};

// Data erasure (ops only): a deleted account is erased permanently, but only after a request from the
// teacher, their school or their district is recorded. Search text is encoded for the same reason as above.
export const erasureDeletedAccountsApi = ({ q } = {}, abortSignal = null) => {
  return Get({ url: "/admin/erasure/deleted-accounts", params: { q: encodeURIComponent(q ?? "") }, abortSignal });
};

export const erasureRequestsApi = ({ status } = {}) => {
  return Get({ url: "/admin/erasure/requests", params: status ? { status } : {} });
};

export const erasureRecordRequestApi = ({ userId, requestedBy, receivedAt, reference }) => {
  return Post({ url: "/admin/erasure/requests", body: { userId, requestedBy, receivedAt, reference } });
};

export const erasurePreviewApi = (id) => {
  return Get({ url: `/admin/erasure/requests/${id}/preview` });
};

// `confirm` is the account's email, or for a district request the district's name, typed exactly.
export const erasureEraseApi = (id, confirm) => {
  return Post({ url: `/admin/erasure/requests/${id}/erase`, body: { confirm } });
};

export const erasureCancelApi = (id) => {
  return Post({ url: `/admin/erasure/requests/${id}/cancel` });
};

// District Hub (ops only): districts and their first owner. Creating a district
// is ops-only on purpose — self-serve is limited to claiming one that exists.
export const districtHubListApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/admin/district-hub/districts", params, abortSignal });
};

// One action: finds (or creates) the district by name and emails its owner an
// invite. ownerEmail receives a single-use, 72h link.
export const createDistrictApi = ({ name, ownerEmail }) => {
  return Post({ url: "/admin/district-hub/districts", body: { name, ownerEmail } });
};

// Invite an owner to a district that is already in the list (e.g. one of the
// imported districts). Addressed by id, so no name has to be retyped. A new
// invite to the same address replaces the earlier link.
export const inviteDistrictOwnerApi = ({ id, ownerEmail }) => {
  return Post({ url: `/admin/district-hub/districts/${id}/invite-owner`, body: { ownerEmail } });
};

// The plan catalog: what each tier costs and how many teachers it covers.
//
// Saving a change creates a NEW version and retires the old one rather than
// editing in place, so "what was Gold when this district signed" stays
// answerable. Schools already on a plan are unaffected — a licence copies the
// seat count at purchase.
export const licensePlansApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/admin/license/plans", params, abortSignal });
};

// Admin-managed custom subscription codes: who can redeem one, how many
// times, and what plan/duration/price it grants. See Neo-backend's
// RedemptionCodeService for the full redemption pre-check.
export const redemptionCodesApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/admin/license/redemption-codes", params, abortSignal });
};

export const getRedemptionCodeDetails = (id) => {
  return Get({ url: `/admin/license/redemption-codes/${id}` });
};

export const saveRedemptionCode = (body) => {
  return Post({ url: "/admin/license/redemption-codes", body });
};

export const updateRedemptionCode = (id, body) => {
  return Put({ url: `/admin/license/redemption-codes/${id}`, body });
};

export const deleteRedemptionCode = (id) => {
  return Delete({ url: `/admin/license/redemption-codes/${id}` });
};

// Book-a-Demo availability. See Neo-backend's DemoAvailabilityService for the
// recurring-template + per-date-override model these calls drive — a date
// the admin has never touched isn't returned as "unavailable", it's whatever
// the recurring template says, computed server-side so this console and the
// public /portal/book-a-demo calendar can never disagree about what's open.
export const getDemoAvailabilitySettings = (abortSignal = null) => {
  return Get({ url: "/admin/demo-availability/settings", abortSignal });
};

export const saveDemoAvailabilitySettings = (body) => {
  return Put({ url: "/admin/demo-availability/settings", body });
};

export const getDemoAvailabilityMonth = (year, month, abortSignal = null) => {
  return Get({ url: "/admin/demo-availability/month", params: { year, month }, abortSignal });
};

export const setDemoDateBlocked = (date, blocked) => {
  return Put({ url: `/admin/demo-availability/date/${date}/block`, body: { blocked } });
};

export const clearDemoDateSlots = (date) => {
  return Delete({ url: `/admin/demo-availability/date/${date}/slots` });
};

export const addDemoTimeSlot = (date, body) => {
  return Post({ url: `/admin/demo-availability/date/${date}/slots`, body });
};

export const updateDemoTimeSlot = (slotId, body) => {
  return Put({ url: `/admin/demo-availability/slots/${slotId}`, body });
};

export const deleteDemoTimeSlot = (slotId) => {
  return Delete({ url: `/admin/demo-availability/slots/${slotId}` });
};

export const saveLicensePlan = (body) => {
  return Post({ url: "/admin/license/plans", body });
};

// Every payment on the platform, newest first.
//
// Keyset paginated on the row id rather than by page number: OFFSET re-reads and
// discards every skipped row, so page 500 of a payments table costs five hundred
// pages of work. This table only grows.
export const licensePaymentsApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/admin/license/payments", params, abortSignal });
};

// How many payments are sitting unresolved. Cheap enough to poll, and it is what
// turns "nobody found out" into a number on a screen somebody already looks at.
export const licensePaymentsNeedingAttention = (abortSignal = null) => {
  return Get({ url: "/admin/license/payments/attention", abortSignal });
};

// Refund a payment. Writes a signed negative row and revokes what it bought —
// both halves happen server-side, so this is one call rather than a sequence a
// half-finished click could leave part-done.
export const refundLicensePayment = (id, body = {}) => {
  return Post({ url: `/admin/license/payments/${id}/refund`, body });
};

export const GetUserById = (id) => {
  return Get({
    url: `/user/${id}`,
  });
};

export const GetUserAffirmations = (params = {}, abortSignal = null) => {
  return Get({ url: "/affirmations", params, abortSignal });
};

// CUSTOMER DETAILS — one teacher's profile, activity and billing (admin only)

export const adminUserOverview = (id) => {
  return Get({ url: `/admin/users/${id}/overview` });
};

export const adminUserActivity = (id, params = {}) => {
  return Get({ url: `/admin/users/${id}/activity`, params });
};

export const adminUserResets = (id, params = {}) => {
  return Get({ url: `/admin/users/${id}/resets`, params });
};

export const adminUserBilling = (id) => {
  return Get({ url: `/admin/users/${id}/billing` });
};

// BLS FEEDBACK

export const analyticalDataAPI = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/summary", params, abortSignal });
};

export const userMetricsApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/user-metrics", params, abortSignal });
};

export const blsListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/bls", params, abortSignal });
};

export const getUserMoods = (params = {}, abortSignal = null) => {
  return Get({ url: "/mood/moods", params, abortSignal });
};

export const getUserTests = (params = {}, abortSignal = null) => {
  return Get({ url: "/test/user-tests", params, abortSignal });
};

// BLS FEEDBACK FORMS

export const feedbackFormListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/bls/feedback-forms", params, abortSignal });
};

export const createFeedbackForm = (bodyData) => {
  return Post({ url: "/bls/feedback-form", body: bodyData });
};

export const updateFeedbackForm = ({ id, bodyData }) => {
  return Patch({ url: `/bls/feedback-form/${id}`, body: bodyData });
};

export const deleteFeedbackForm = (id) => {
  return Delete({ url: `/bls/feedback-form/${id}` });
};

export const updateUserProfile = ({ id, bodyData }) => {
  return Patch({ url: `/user/${id}`, body: bodyData });
};

export const MediaUpload = (bodyData, abortSignal = null) => {
  const token = store.getState().userAuth.user.Token;

  const url = `${Config.env().API_URL}/media/upload`;

  return fetch(url, {
    headers: {
      authorization: token,
    },
    body: bodyData,
    method: "POST",
  }).then(handleFetchError);
};

// NOTIFICATIONS MANAGEMENT

export const createScheduledNotification = (bodyData) => {
  return Post({
    url: "/scheduled-notifications",
    body: bodyData,
  });
};

export const getScheduledNotifications = (params = {}, abortSignal = null) => {
  return Get({ url: "/scheduled-notifications", params, abortSignal });
};

export const getScheduledNotificationById = (id) => {
  return Get({
    url: `/scheduled-notifications/${id}`,
  });
};

export const updateScheduledNotification = ({ id, bodyData }) => {
  return Put({
    url: `/scheduled-notifications/${id}`,
    body: bodyData,
  });
};

export const deleteScheduledNotification = (id) => {
  return Delete({
    url: `/scheduled-notifications/${id}`,
  });
};

// AUDIO/VIDEO CONTENT LIBRARY

export const getAudioVideoContents = (params = {}, abortSignal = null) => {
  return Get({ url: "/audio-video-content", params, abortSignal });
};

export const getAudioVideoContentById = (id) => {
  return Get({ url: `/audio-video-content/${id}` });
};

export const createAudioVideoContent = (bodyData) => {
  return Post({ url: "/audio-video-content", body: bodyData });
};

export const updateAudioVideoContent = ({ id, bodyData }) => {
  return Patch({ url: `/audio-video-content/${id}`, body: bodyData });
};

// Soft delete — moves it to the bin (status=BIN in getAudioVideoContents).
export const deleteAudioVideoContent = (id) => {
  return Delete({ url: `/audio-video-content/${id}` });
};

export const restoreAudioVideoContent = (id) => {
  return Post({ url: `/audio-video-content/${id}/restore` });
};

// Hard delete — only allowed once the item is already in the bin; the
// backend rejects this (with a message surfaced automatically by
// handleFetchError's toast) if the content has real play/favorite history.
export const permanentlyDeleteAudioVideoContent = (id) => {
  return Delete({ url: `/audio-video-content/${id}/permanent` });
};

// LOCKED SESSION CONTENT — Calm (Micro Reset) and Focus (breathing), the two
// built-in sessions with no AudioVideoContent row of their own. stateKey is
// always "calm" or "focus".
export const getLockedSessionContent = (stateKey) => {
  return Get({ url: `/locked-session-content/${stateKey}` });
};

export const updateLockedSessionContent = ({ stateKey, bodyData }) => {
  return Patch({ url: `/locked-session-content/${stateKey}`, body: bodyData });
};

// The built-in resets (Big Hug Squeeze, Hand Compression, Thumb Peek, Color
// Hunt, Cross-Body Switch, Standing Cross March, Seated Cross Crawl, Cross-Crawl
// Tap, Alternating Taps): name,
// copy, picture and background music, one
// record per activity.
export const getResetActivityContents = () => {
  return Get({ url: `/built-in-activity-content` });
};

export const getResetActivityContent = (activity) => {
  return Get({ url: `/built-in-activity-content/${activity}` });
};

export const updateResetActivityContent = ({ activity, bodyData }) => {
  return Patch({ url: `/built-in-activity-content/${activity}`, body: bodyData });
};

export const getExportUserListing = (params = {}, abortSignal = null) => {
  const token = store.getState().userAuth.user.Token;
  const url = `${Config.env().API_URL}/users/export`;

  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  return fetch(fullUrl, {
    method: "GET",
    headers: {
      Authorization: token,
    },
    signal: abortSignal,
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  });
};

// CLICK ANALYTICS

export const getClickAnalyticsListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/deep-link-analytics", params, abortSignal });
};

export const getClickAnalyticsDetailsByDate = (params) => {
  return Get({ url: "/deep-link-analytics/details", params });
};

// MCAP — Mind Circuit Analytics Platform

// ---- Section A: org totals, adoption ------------------------------------
export const getMcapOrgTotals = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/org-totals", params, abortSignal });
};

export const getMcapAdoptionMetrics = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/adoption-metrics", params, abortSignal });
};

export const getMcapAdoptionFlags = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/adoption-flags", params, abortSignal });
};

// ---- Section B: implementation, settings, school view --------------------
// MCAP settings — the administrator-tunable assumptions behind published
// figures (off-task baseline, multi-setting threshold, aggregation floor).
// GET always returns values: the backend upserts defaults on first read, so
// there is no "not configured yet" state to handle here.
export const getMcapSettings = (abortSignal = null) => {
  return Get({ url: "/analytics/mcap-settings", abortSignal });
};

// Partial update — send only the fields being changed; omitted ones keep
// their stored value.
export const updateMcapSettings = (bodyData) => {
  return Put({ url: "/analytics/mcap-settings", body: bodyData });
};

// Implementation fidelity + instructional time. Every rate field can come
// back null, meaning "not enough data to report" — never zero. The UI must
// distinguish the two.
export const getMcapImplementationSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-summary", params, abortSignal });
};

// The classrooms behind one reset moment — opened from a row on the moment
// table. Takes the dashboard's own filters plus `context`.
export const getMcapImplementationMoment = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-moment", params, abortSignal });
};

// Classrooms using resets at exactly one moment — the list behind the
// "needs support" tile.
export const getMcapImplementationSingleMoment = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-single-moment", params, abortSignal });
};

// Schools ranked by resets in the window — what the School view picker shows
// before the admin types anything.
export const getMcapRankedSchools = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/schools/ranked", params, abortSignal });
};

// The extra detail one school earns: playback funnel + per-activity breakdown.
export const getMcapSchoolView = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/school-view", params, abortSignal });
};

// The printable summary. Fetched as a blob rather than opened in a new tab
// because the endpoint needs an Authorization header, which a plain
// window.open cannot send.
export const getMcapImplementationReport = (params = {}) => {
  const token = store.getState().userAuth.user.Token;
  const queryString = new URLSearchParams(params).toString();
  const url = `${Config.env().API_URL}/analytics/implementation-report${queryString ? `?${queryString}` : ""}`;

  return fetch(url, { method: "GET", headers: { Authorization: token } }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  });
};

export const getMcapEngagementSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/engagement/summary", params, abortSignal });
};

export const getMcapWellbeingSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/wellbeing/summary", params, abortSignal });
};

export const getMcapContentSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/summary", params, abortSignal });
};

export const getMcapStateTrends = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/state-trends", params, abortSignal });
};

export const getMcapUsageTrends = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/usage-trends", params, abortSignal });
};

export const getMcapActivityRanking = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/activity-ranking", params, abortSignal });
};

export const getMcapLicensingSummary = (abortSignal = null) => {
  return Get({ url: "/analytics/licensing/summary", abortSignal });
};

export const getMcapTimeOfDay = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/time-of-day", params, abortSignal });
};

export const getMcapScreenViews = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/screen-views", params, abortSignal });
};

export const getMcapSectionSummary = (sectionId) => {
  return Get({ url: `/analytics/org/section/${sectionId}` });
};

export const getMcapSchoolOptions = () => {
  return Get({ url: "/analytics/org/schools" });
};

export const getMcapDashboard = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/dashboard", params, abortSignal });
};

export const getMcapExportUrl = (params = {}) => {
  const token = store.getState().userAuth.user.Token;
  const url = `${Config.env().API_URL}/analytics/export`;
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  return fetch(fullUrl, {
    method: "GET",
    headers: { Authorization: token },
  }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  });
};
